import { useState } from 'react'
import { Icon } from '@iconify/react'
import Modal from '../../../../components/modal/Modal'

interface SecretModalProps {
  secret: string
  rotated: boolean
  close: () => void
}

/**
 * Shown once, immediately after the secret is issued.
 *
 * There is no endpoint that reads a secret back, so if this closes without the
 * value being saved it is gone for good and the only way out is rotating.
 * That is why closing is gated behind an explicit confirmation.
 */
function SecretModal({ secret, rotated, close }: SecretModalProps) {
  const [copied, setCopied] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  return (
    <Modal position='center'>
      <div className='webhook-secret-modal'>
        <h1>
          {rotated ? 'New signing secret' : 'Your signing secret'}
        </h1>
        <p className='warn'>
          <Icon icon='mdi:alert' /> This is the only time we will show you this
          secret. Copy it into your server configuration now — we cannot
          retrieve it later, only replace it.
        </p>

        <div className='secret-box flex items-center justify-between'>
          <code>{secret}</code>
          <Icon
            icon={copied ? 'mdi:check' : 'charm:clipboard'}
            onClick={() => {
              navigator.clipboard.writeText(secret)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
          />
        </div>
        {copied && <span className='copied'>Copied to clipboard</span>}

        {rotated && (
          <p className='note'>
            The previous secret has already stopped working. Deploy this one
            before your next shipment status change.
          </p>
        )}

        <label className='ack flex items-center'>
          <input
            type='checkbox'
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>I have saved this secret somewhere safe</span>
        </label>

        <div className='buttons flex justify-center'>
          <button
            className='proceed'
            disabled={!acknowledged}
            onClick={close}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SecretModal
